import { z } from 'zod';

import { UserCountryCode } from '@shared/types/countryCodes.ts';

export const VAT_NUMBER_MAX_LENGTH: Record<UserCountryCode, number> = {
  IT: 11,
  ES: 9,
  DE: 11,
};

const SPANISH_VAT_NUMBER_PATTERN = /^([A-Z]\d{8}|\d{8}[A-Z]|[A-Z]\d{7}[A-Z])$/;
const ITALIAN_VAT_NUMBER_PATTERN = /^\d{11}$/;
const GERMAN_VAT_NUMBER_PATTERN = /^DE\d{9}$/;

function normalizeVatNumber(value: string): string {
  return value.trim();
}

export function isValidItalianVatNumber(value: string): boolean {
  return ITALIAN_VAT_NUMBER_PATTERN.test(normalizeVatNumber(value));
}

export function isValidGermanVatNumber(value: string): boolean {
  return GERMAN_VAT_NUMBER_PATTERN.test(normalizeVatNumber(value).toUpperCase());
}

export function isValidSpanishVatNumber(value: string): boolean {
  return SPANISH_VAT_NUMBER_PATTERN.test(normalizeVatNumber(value).toUpperCase());
}

export function isValidVatNumber(value: string, countryCode: UserCountryCode): boolean {
  switch (countryCode) {
    case 'IT':
      return isValidItalianVatNumber(value);
    case 'DE':
      return isValidGermanVatNumber(value);
    case 'ES':
      return isValidSpanishVatNumber(value);
  }
}

export function getVatValidationErrorMessage(countryCode: UserCountryCode, value: string): string | null {
  if (isValidVatNumber(value, countryCode)) {
    return null;
  }

  const normalizedValue = normalizeVatNumber(value);

  switch (countryCode) {
    case 'IT':
      if (!/^\d+$/.test(normalizedValue)) {
        return 'errorMessages.numericalValueRequired';
      }
      return 'errorMessages.vatNumberMustBe11Digits';
    case 'DE':
      return 'errorMessages.invalidDEVatNumber';
    case 'ES':
      return 'errorMessages.invalidESVatNumber';
  }
}

export function createVatNumberFieldValidator(countryCode: UserCountryCode) {
  return z
    .string({ required_error: 'errorMessages.required' })
    .min(1, { message: 'errorMessages.required' })
    .superRefine((value, context) => {
      const errorMessage = getVatValidationErrorMessage(countryCode, value);

      if (errorMessage) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: errorMessage,
        });
      }
    });
}
